import { ref, watchEffect } from 'vue'

import {
  type HttpEndpoint,
  type HttpError,
  cartAPI,
} from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

export function useAddToCart() {
  const idBeingAddedToCart = ref<string>()
  const addingToCart = ref<'pending' | 'idle' | 'success' | 'error'>('idle')
  const errorAddingToCart = ref<HttpError | null>()
  const { trackErrors } = useTracking()

  function useCartAPIFetch(
    cartAPIEndpoint: HttpEndpoint<unknown>,
    body: { listings: { listing_id: string }[] } | { listing_id: string },
  ) {
    const { execute, status, error } = useHttpFetch(cartAPIEndpoint, {
      immediate: false,
      body,
    })

    watchEffect(() => {
      addingToCart.value = status?.value
      errorAddingToCart.value = error?.value
      if (status.value === 'error' && error.value) {
        trackErrors({
          zone: 'bundle_add_to_cart',
          message: error.value.message,
        })
      }
    })

    return execute()
  }

  function addToCart(listingId: string) {
    idBeingAddedToCart.value = listingId

    return useCartAPIFetch(cartAPI.addToCart, { listing_id: listingId })
  }

  async function addAllToCart(listingIds: string[]) {
    return useCartAPIFetch(cartAPI.addProductsToCart, {
      listings: listingIds.map((id) => ({ listing_id: id })),
    })
  }

  return {
    addToCart,
    addAllToCart,
    idBeingAddedToCart,
    addingToCart,
    errorAddingToCart,
  }
}
